/**
 * Geldfluss-Radar-Karte — „Wohin fließt das große Geld?"
 *
 * Zeigt die Fußspuren der großen Adressen: Vorsprung jedes Topfs vs. S&P 500.
 * Wichtigste Zeile zuerst: „dreht gerade auf" = Geld beginnt zu fließen =
 * Frühsignal (früh dran statt am Ende auf den Zug springen). Signalgeber
 * (Dollar, Anleihen) mit Klartext-Deutung fürs Umfeld.
 */

import { Waves } from 'lucide-react';

import { cn } from '@/lib/utils';
import { CARD, CARD_HEADER } from '@/lib/design-tokens';
import type { FlowPot, FlowRadar, FlowState } from '@/lib/money-flow-radar';

const STATE_META: Record<
  FlowState,
  { label: string; chip: string }
> = {
  dreht_auf: {
    label: 'dreht auf',
    chip: 'border-amber-600/60 bg-amber-950/40 text-amber-200',
  },
  zufluss: {
    label: 'Geld fließt rein',
    chip: 'border-emerald-700/60 bg-emerald-950/30 text-emerald-200',
  },
  seitwaerts: {
    label: 'unauffällig',
    chip: 'border-zinc-700/60 bg-zinc-900/40 text-zinc-400',
  },
  dreht_ab: {
    label: 'Zufluss bröckelt',
    chip: 'border-orange-700/60 bg-orange-950/30 text-orange-200',
  },
  abfluss: {
    label: 'Geld fließt raus',
    chip: 'border-red-800/60 bg-red-950/25 text-red-300',
  },
  unbekannt: {
    label: 'keine Daten',
    chip: 'border-zinc-800/60 bg-zinc-900/20 text-zinc-600',
  },
};

function fmtPp(v: number | null): string {
  if (v === null) return '–';
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}`;
}

function PotRow({ pot }: { pot: FlowPot }) {
  const meta = STATE_META[pot.state];
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-1 min-w-0">
      {/* Mobile-Fix 13.07.: min-w-0 + flex-1 statt fixer w-36 → kein 375px-Überlauf. */}
      <div className="min-w-0 flex-1 basis-32 truncate text-xs text-zinc-200">
        {pot.label}
        <span className="ml-1 text-[9px] text-zinc-600">{pot.symbol}</span>
      </div>
      <span
        className={cn(
          'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-medium',
          meta.chip
        )}
      >
        {meta.label}
        {pot.hot && <span className="ml-1 text-amber-300">· heiß, spät</span>}
      </span>
      <div className="ml-auto shrink-0 text-right font-mono text-[11px]">
        <span
          className={cn(
            pot.rs13 === null
              ? 'text-zinc-600'
              : pot.rs13 >= 0
                ? 'text-emerald-300'
                : 'text-red-300'
          )}
        >
          {fmtPp(pot.rs13)}
        </span>
        <span className="ml-1 text-[9px] text-zinc-500">13 Wo</span>
        <span className="ml-2 text-zinc-500">{fmtPp(pot.rs26)}</span>
        <span className="ml-1 text-[9px] text-zinc-600">26 Wo</span>
      </div>
    </div>
  );
}

interface Props {
  radar: FlowRadar;
  isLoading?: boolean;
}

export function GeldflussRadarCard({ radar, isLoading }: Props) {
  if (!radar.known) {
    return (
      <section className={CARD}>
        <h3 className={CARD_HEADER}>
          <Waves className="size-4 text-sky-400" /> Geldfluss-Radar
        </h3>
        <p className="text-xs text-zinc-500">
          {isLoading
            ? 'Lädt Kursdaten für Gold, Bitcoin, Sektoren, Regionen …'
            : 'Noch zu wenig Kursdaten — das Radar füllt sich, sobald die Töpfe geladen sind.'}
        </p>
      </section>
    );
  }

  const frueh = radar.pots.filter((p) => p.state === 'dreht_auf');
  const laufen = radar.pots.filter((p) => p.state === 'zufluss');
  const raus = radar.pots.filter((p) => p.state === 'dreht_ab' || p.state === 'abfluss');
  const still = radar.pots.filter(
    (p) => p.state === 'seitwaerts' || p.state === 'unbekannt'
  );

  return (
    <section className={CARD}>
      <h3 className={CARD_HEADER}>
        <Waves className="size-4 text-sky-400" /> Geldfluss-Radar — wohin fließt das
        große Geld?
      </h3>
      <p className="mb-3 text-[11px] leading-relaxed text-zinc-500">
        Große Adressen hinterlassen Spuren: Läuft ein Topf besser als der S&amp;P 500,
        fließt Geld hinein — oft lange bevor die Nachricht offiziell ist.{' '}
        <span className="text-amber-300/90">„dreht auf"</span> = 26 Wochen hinten,
        letzte 13 Wochen vorn → Geld beginnt gerade zu fließen. Das ist das
        Frühsignal.
      </p>

      <div className="space-y-3">
        {frueh.length > 0 && (
          <div>
            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300/90">
              🟡 Früh dran — dreht gerade nach oben
            </div>
            {frueh.map((p) => (
              <PotRow key={p.symbol} pot={p} />
            ))}
          </div>
        )}

        {laufen.length > 0 && (
          <div>
            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300/90">
              🟢 Läuft schon — Geld ist drin
            </div>
            {laufen.map((p) => (
              <PotRow key={p.symbol} pot={p} />
            ))}
          </div>
        )}

        {raus.length > 0 && (
          <div>
            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-300/80">
              🔴 Geld zieht ab
            </div>
            {raus.map((p) => (
              <PotRow key={p.symbol} pot={p} />
            ))}
          </div>
        )}

        {still.length > 0 && (
          <details className="group/still">
            <summary className="cursor-pointer list-none text-[10px] font-semibold uppercase tracking-wide text-zinc-500 hover:text-zinc-400">
              <span className="inline-block transition-transform group-open/still:rotate-90">
                ▸
              </span>{' '}
              ⚪ Unauffällig ({still.length})
            </summary>
            {still.map((p) => (
              <PotRow key={p.symbol} pot={p} />
            ))}
          </details>
        )}

        {radar.signale.length > 0 && (
          <div className="border-t border-zinc-800/60 pt-2">
            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-300/80">
              Signalgeber — was das Umfeld sagt
            </div>
            {radar.signale.map((p) => (
              <div key={p.symbol} className="py-0.5 text-[11px] text-zinc-400">
                <span className="text-zinc-200">{p.label}:</span>{' '}
                {p.signal_hint ?? STATE_META[p.state].label}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-2 text-[10px] text-zinc-600">
        Zahlen = Vorsprung vs. S&amp;P 500 in Prozentpunkten (13 bzw. 26 Wochen).
      </div>
    </section>
  );
}
